import { Box, Checkbox, Flex, Paper, Text } from '@mantine/core';
import { useEffect, useMemo } from 'react';
import { StepCard } from 'src/UI/generate-flow/StepCard';
import { SelectableList } from 'src/UI/generate-flow/SelectableList';
import { useClients } from 'src/clients/hooks/useClients';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { DataExportStepProps } from '../types';

const MAX_DATA_EXPORT_MEMBERS = 2;

type Member = {
  clientId: string;
  name: string;
};

export function DataExportMembersStep({ data, setData }: DataExportStepProps) {
  const { clients } = useClients();

  const members: Member[] = useMemo(() => {
    const familyMembers = data.family?.members ?? [];
    return familyMembers.map(({ clientId }) => {
      const client = clients.find(c => c.id === clientId);
      const name = client ? `${client.firstName} ${client.lastName}`.trim() : 'Unknown client';
      return { clientId, name };
    });
  }, [data.family?.members, clients]);

  const selected = data.selectedMemberClientIds ?? [];

  // Default to the first two members (the export's primary and secondary) so the
  // step mirrors the previous behaviour while letting advisers change the pick.
  useEffect(() => {
    if (data.selectedMemberClientIds !== undefined) return;
    if (members.length === 0) return;
    setData(prev => {
      if (prev.selectedMemberClientIds !== undefined) return prev;
      return {
        ...prev,
        selectedMemberClientIds: members.slice(0, MAX_DATA_EXPORT_MEMBERS).map(m => m.clientId),
      };
    });
  }, [members, data.selectedMemberClientIds, setData]);

  const toggle = (clientId: string, checked: boolean) => {
    setData(prev => {
      const current = prev.selectedMemberClientIds ?? [];
      const next = checked ? [...current, clientId] : current.filter(id => id !== clientId);
      return { ...prev, selectedMemberClientIds: next };
    });
  };

  return (
    <Flex direction="column" h="100%" mih={0}>
      <StepCard
        title="Select the family members to include"
        subTitle="A data export covers up to two people. Choose which members to include."
      >
        <SelectableList
          items={members}
          getKey={m => m.clientId}
          renderRow={member => {
            const isSelected = selected.includes(member.clientId);
            const disabled = !isSelected && selected.length >= MAX_DATA_EXPORT_MEMBERS;
            return (
              <MemberRow
                member={member}
                selected={isSelected}
                disabled={disabled}
                onToggle={toggle}
              />
            );
          }}
        />
      </StepCard>
    </Flex>
  );
}

type MemberRowProps = {
  member: Member;
  selected: boolean;
  disabled: boolean;
  onToggle: (clientId: string, checked: boolean) => void;
};

function MemberRow({ member, selected, disabled, onToggle }: MemberRowProps) {
  const handleToggle = () => {
    if (disabled) return;
    onToggle(member.clientId, !selected);
  };

  return (
    <Paper
      radius="12"
      withBorder
      px="md"
      py="sm"
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={handleToggle}
      styles={theme => ({
        root: {
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          borderColor: selected ? theme.colors.brand[6] : 'var(--default-border-color)',
          boxShadow: 'none',
          userSelect: 'none',
        },
      })}
    >
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          columnGap: 'var(--mantine-spacing-xxs)',
        }}
      >
        <Flex align="center" gap="md" miw={0}>
          <Checkbox
            checked={selected}
            disabled={disabled}
            onClick={event => event.stopPropagation()}
            onChange={() => onToggle(member.clientId, !selected)}
            color="brand"
            size="md"
            iconColor="white"
          />
          <Text fw={FONT_WEIGHTS.semibold} size="sm" truncate c="gray.8">
            {member.name}
          </Text>
        </Flex>
      </Box>
    </Paper>
  );
}
